// import './polyfills';
import Logger from '../utils/logger';
import Advertising from './advertising';
import Analytics from './analytics';
import localStorage from '../utils/localStorage';
import AdsWizz from './advertising/adswizz';

// @todo instead of this long list, import to Constants: import {* as Constants} from './constants'
import {
  EVENT_AD_END,
  EVENT_AD_SKIP,
  EVENT_AD_START,
  EVENT_CANPLAY,
  EVENT_COMPLETE,
  EVENT_CUE_CHANGE,
  EVENT_DURATION_CHANGE,
  EVENT_EMPTY,
  EVENT_ERROR,
  EVENT_LANGUAGES_CHANGE,
  EVENT_LANGUAGE_CHANGE,
  EVENT_LEVELS_CHANGE,
  EVENT_LEVEL_CHANGE,
  EVENT_LOADED,
  EVENT_LOADING,
  EVENT_LOAD_START,
  EVENT_NOWPLAYING_CHANGE,
  EVENT_PAUSE,
  EVENT_PAUSED,
  EVENT_PLAY,
  EVENT_PLAYBACK_RATE_CHANGE,
  EVENT_PLAYING,
  EVENT_PLAYLIST_CHANGE,
  EVENT_PLAYLIST_ITEM_CHANGE,
  EVENT_PLAYLIST_LOAD,
  EVENT_POSITION,
  EVENT_PROGRESS,
  EVENT_READY,
  EVENT_REPEAT_CHANGE,
  EVENT_RESUME,
  EVENT_SEEK,
  EVENT_SEEKED,
  EVENT_SEEKABLE,
  EVENT_SOURCE_CHANGE,
  EVENT_STATE,
  EVENT_STOP,
  EVENT_TRACKING,
  EVENT_TRACKS_CHANGE,
  EVENT_TRACK_LOAD,
  EVENT_UNLOAD,
  EVENT_VOLUME_CHANGE,
  EVENT_WAITING,
  EVENT_DOWNLOAD,
  EVENT_DESTROY,
  METADATA_TYPE_STREAM,
  REPEAT_MODE_NONE,
  REPEAT_MODE_PLAYLIST,
  REPEAT_MODE_SINGLE,
  STATE_ADPLAYING,
  STATE_EMPTY,
  STATE_ERROR,
  STATE_IDLE,
  STATE_LOADING,
  STATE_NOT_READY,
  STATE_PAUSED,
  STATE_PLAYING,
  STATE_WAITING,
  EVENT_AD_SKIPPABLE,
  EVENT_AD_COMPANION_START,
  EVENT_AD_COMPANION_END,
  EVENT_AD_BREAK_START,
  EVENT_AD_BREAK_END,
  VERSION,
  EVENT_LOADED_METADATA,
  METADATA_TYPE_ADSWIZZ_STREAM,
  METADATA_TYPE_METADATA,
  METADATA_TYPE_NOWPLAYING
} from './constants';
import EventEmitter from './event-emitter';
import NowPlaying from './now-playing';
import CorePlayer from './player';
import Settings from './settings';
import { createQualityLevel, createTextTrack, createTextTrackCue } from './utils/models';
import environment from './utils/environment';
import uuid from '../utils/uuid';
import sid from '../utils/sid';
import referrer from '../utils/referrer';
import computedFields from './utils/computedFields';
import isString from './utils/is-string';
import formatSeconds from '../utils/format-seconds';
import filename from '../utils/filename';
import { mergeOptions, validateOptions, defaultOptions } from '../utils/options';
import PlayerError from './utils/errors';
import Timeline from './controllers/timeline';
import { loadRemoteTextTrack, parseVttData } from './controllers/text-tracks';
import filesize from 'filesize';

const STORAGE_KEY = 'iono-player-settings';

// create a copy of an event and prefix it's event type
function prefixEvent (prefix, event) {
  return {
    ...event,
    type: `${prefix}.${event.type}`
  };
}

// helpers to prefixed events
const prefixAdvertisingEvent = prefixEvent.bind(null, 'adverts');
const prefixPlayerEvent = prefixEvent.bind(null, 'content');

// transition a state machine in to a new state - called with the player api as 'this'
function transition (stateMachine, newState, event) {
  if (newState === stateMachine.state) return stateMachine.state;

  const previousStateName = stateMachine.state;
  const previousState = stateMachine.states[stateMachine.state];
  const currentState = stateMachine.states[newState];

  if (!currentState) {
    throw new Error(`Failed to transition states: Invalid state '${newState}'`);
  }

  // execute the exit hook on the previous state
  if (typeof previousState.onExit === 'function') {
    previousState.onExit.call(stateMachine, stateMachine.context, event);
  }

  stateMachine.state = newState;

  // execute the enter hook on the current state
  if (typeof currentState.onEnter === 'function') {
    currentState.onEnter.call(stateMachine, stateMachine.context, event);
  }

  stateMachine.emit({
    type: StateEvents.Transition,
    currentState: stateMachine.state,
    previousState: previousStateName
  });

  return stateMachine.state;
}

// send and event / action in to a state machine - called with the player api as 'this'
function send (stateMachine, event) {
  if (typeof event === 'string') {
    event = { type: event };
  }

  const currentState = stateMachine.states[stateMachine.state];

  if (!currentState) {
    throw new Error(`Unrecognized state '${stateMachine.state}'`);
  }

  let callback;

  // look for an event handler on the current state
  if (currentState.events && currentState.events[event.type]) {
    callback = currentState.events[event.type];
  }
  // look for an event handler on the state machine
  else if (stateMachine.events && stateMachine.events[event.type]) {
    callback = stateMachine.events[event.type];
  }

  // transition
  if (typeof callback === 'string') {
    return stateMachine.transition(callback, event);
  }

  // callback
  if (typeof callback === 'function') {
    return callback.call(stateMachine, stateMachine.context, event);
  }
}

// These actions come from the api and usually imply some form of user / script interaction with the api
const ApiActions = {
  Play: 'api.action_play',
  Pause: 'api.action_pause',
  Stop: 'api.action_stop',
  Load: 'api.action_load',
  LoadMore: 'api.action_load_more',
  Add: 'api.action_add',
  Unload: 'api.action_unload',
  Mute: 'api.action_mute',
  Unmute: 'api.action_unmute',
  NextPlaylistItem: 'api.action_next_playlist_item',
  PreviousPlaylistItem: 'api.action_previous_playlist_item',
  SelectPlaylistItem: 'api.action_select_playlist_item',
  SetPosition: 'api.action_set_position',
  SetPlaybackRate: 'api.action_set_playback_rate',
  SetVolume: 'api.action_set_volume',
  SetQualityLevel: 'api.action_set_quality_level',
  Skip: 'api.action_skip'
};

// Events emitted by the content player
const ContentEvents = {
  Loading: `content.${EVENT_LOADING}`,
  Waiting: `content.${EVENT_WAITING}`,
  Playing: `content.${EVENT_PLAYING}`,
  Error: `content.${EVENT_ERROR}`,
  LoadStart: `content.${EVENT_LOAD_START}`,
  Ended: `content.${EVENT_COMPLETE}`,
  TimeUpdate: `content.${EVENT_POSITION}`,
  Progress: `content.${EVENT_PROGRESS}`,
  DurationChange: `content.${EVENT_DURATION_CHANGE}`,
  Tracking: `content.${EVENT_TRACKING}`,
  Play: `content.${EVENT_PLAY}`,
  Pause: `content.${EVENT_PAUSE}`,
  Stop: `content.${EVENT_STOP}`,
  CanPlay: `content.${EVENT_CANPLAY}`,
  RateChange: `content.${EVENT_PLAYBACK_RATE_CHANGE}`,
  PlaylistSelect: `content.${EVENT_PLAYLIST_ITEM_CHANGE}`,
  PlaylistChange: `content.${EVENT_PLAYLIST_CHANGE}`,
  PlaylistLoad: `content.${EVENT_PLAYLIST_LOAD}`,
  SourceChange: `content.${EVENT_SOURCE_CHANGE}`,
  LevelsChange: `content.${EVENT_LEVELS_CHANGE}`,
  LevelChange: `content.${EVENT_LEVEL_CHANGE}`,
  TextTrackLoad: `content.${EVENT_TRACK_LOAD}`,
  TextTracksChange: `content.${EVENT_TRACKS_CHANGE}`,
  TextTrackCueChange: `content.${EVENT_CUE_CHANGE}`,
  LanguageChange: `content.${EVENT_LANGUAGE_CHANGE}`,
  LanguagesChange: `content.${EVENT_LANGUAGES_CHANGE}`,
  LoadedMetadata: `content.${EVENT_LOADED_METADATA}`,
  VolumeChange: `content.${EVENT_VOLUME_CHANGE}`,
  Seeking: `content.${EVENT_SEEK}`,
  Seeked: `content.${EVENT_SEEKED}`,
  Paused: `content.${EVENT_PAUSED}`,
  Resume: `content.${EVENT_RESUME}`,
  Loaded: `content.${EVENT_LOADED}`,
  NowPlayingChange: `content.${EVENT_NOWPLAYING_CHANGE}`,
  Destroy: `content.${EVENT_DESTROY}`
};

// Events emitted by the adverts player
const AdvertEvents = {
  Loading: `adverts.${EVENT_LOADING}`,
  Waiting: `adverts.${EVENT_WAITING}`,
  Playing: `adverts.${EVENT_PLAYING}`,
  Error: `adverts.${EVENT_ERROR}`,
  LoadStart: `adverts.${EVENT_LOAD_START}`,
  Ended: `adverts.${EVENT_COMPLETE}`,
  TimeUpdate: `adverts.${EVENT_POSITION}`,
  Progress: `adverts.${EVENT_PROGRESS}`,
  DurationChange: `adverts.${EVENT_DURATION_CHANGE}`,
  Tracking: `adverts.${EVENT_TRACKING}`,
  Start: `adverts.${EVENT_AD_START}`,
  End: `adverts.${EVENT_AD_END}`,
  Skip: `adverts.${EVENT_AD_SKIP}`,
  Skippable: `adverts.${EVENT_AD_SKIPPABLE}`,
  Play: `adverts.${EVENT_PLAY}`,
  Pause: `adverts.${EVENT_PAUSE}`,
  CanPlay: `adverts.${EVENT_CANPLAY}`,
  PlaylistLoad: `adverts.${EVENT_PLAYLIST_LOAD}`,
  PlaylistSelect: `adverts.${EVENT_PLAYLIST_ITEM_CHANGE}`,
  PlaylistChange: `adverts.${EVENT_PLAYLIST_CHANGE}`,
  SourceChange: `adverts.${EVENT_SOURCE_CHANGE}`,
  CompanionStart: `adverts.${EVENT_AD_COMPANION_START}`,
  CompanionEnd: `adverts.${EVENT_AD_COMPANION_END}`,
  BreakStart: `adverts.${EVENT_AD_BREAK_START}`,
  BreakEnd: `adverts.${EVENT_AD_BREAK_END}`
};

const StateEvents = {
  Transition: 'transition'
};

let lastPlayerId = 0;
export const players = {};

const settings = new Settings({
  storageKey: STORAGE_KEY,
  storage: localStorage
});

function Player (options) {
  if (!Player.supported()) {
    Logger.warn('Playback not supported in this environment, please try a different browser');
    return;
  }

  options = mergeOptions(options || {});
  const optionsValidationResult = validateOptions(options);

  if (!optionsValidationResult.valid) {
    throw new Error('Unable to create player instance, one or more options are invalid: \n * ' + optionsValidationResult.errors.join('\n * '));
  }

  this.id = ++lastPlayerId;
  players[this.id] = this;

  // expose options for plugins
  this.options = options;

  const api = this;
  const playerSid = sid();
  const events = new EventEmitter();
  const computed = computedFields(this);

  // create a logger instance for this player
  const logger = Logger.create({
    level: Logger.DEBUG
  });

  // update the context object with playback context
  function loggingContext (context) {
    context.tags = `${__VERSION__},player`; /* eslint-disable-line */
    context.player_version = __VERSION__; /* eslint-disable-line */
    context.referrer = referrer();
    if (SM && SM.state !== STATE_NOT_READY) {
      context.tags += `,${computed.get('contentType')},${computed.get('format')}`;
      context.content_type = computed.get('contentType');
      context.format = computed.get('format');
    }
  }

  // override the logging function to handle some advanced cases
  logger.log = function (message, level) {
    let debugLevel = null;

    // parse the debug level from the level string
    if (typeof level === 'string' && level.substr(0, 5) === 'debug') {
      debugLevel = (level.substr(5) !== '') ? parseInt(level.substr(5), 10) : 1;
      level = logger.DEBUG;
    } else if (level === logger.DEBUG) {
      // all debug messages are considered level 1 debug message
      debugLevel = 1;
    }

    // ignore debug messages if they exceed the configured debug level threshold
    if (debugLevel !== null && debugLevel > api.options.debug) {
      return;
    }

    // @todo get context for this log message (content_type, format)
    logger.withContext(function (context) {
      loggingContext(context);
      // prepend the player id to all messages before logging
      Logger.log.call(logger, `[iono.Player:${api.id}] ${message}`, level);
    });
  };

  // override the logException function to add player context to log messages
  logger.logException = function (error) {
    logger.withContext(function (context) {
      loggingContext(context);
      // GH: Commented out because it caused errors when attempting to modify the message of a DOMException
      // error.message = `[iono.Player:${api.id}] ${error.message}`;
      Logger.logException.call(logger, error);
    });
  };

  options.playbackRate = settings.get('playbackRate') || options.playbackRate;
  options.qualityLevel = settings.get('qualityLevel') || options.qualityLevel;
  options.volume = settings.get('volume') || options.volume;
  options.preload = settings.get('preload') || options.preload;
  options.autoplay = settings.get('autoplay') || options.autoplay;
  options.repeat = settings.get('repeat') || options.repeat;
  options.language = settings.get('language') || options.language;

  options.computed = computed;

  let advertising = null;

  const content = new CorePlayer({ logger, ...options });
  const _timeline = new Timeline({
    logger: logger,
    player: content
  });
  const _nowPlaying = new NowPlaying({
    logger: logger,
    maxHistory: options.nowPlaying.maxHistory,
    timeline: _timeline,
    player: content,
    fetchArtwork: options.nowPlaying.fetchArtwork,
    interval: options.nowPlaying.interval
  });
  const _adswizz = new AdsWizz({
    ...options.adswizz,
    logger,
    timeline: _timeline,
    player: content,
    gdpr: options.gdpr
  });

  const readyQueue = [];

  // Fix for Safari and iOS where playback breaks if both audio elements aren't initialized with the first
  // user interaction, so we call load() on both audio instances inside the ui handler, which appears
  // to unlock both audio instances for the remainder of the page session.
  function _primePlayer (ctx) {
    if (!ctx.userInteracted) {
      logger.log('Priming content and advertising players', 'debug2');
      ctx.userInteracted = true;
      if (advertising) advertising.player.getMedia().load();
      content.getMedia().load();
    }
  }

  // Add the current playing content uid to all events emitted by the api
  events.emit = function _addContentUidToEvent (event) {
    if (typeof event === 'string') {
      event = { type: event };
    }

    if (event.type === 'state') {
      logger.log(`State: ${event.previousState} => ${event.currentState}`, 'debug1');
    } else if (event.type === 'tracking') {
      if (event.details.event === 'progress') {
        const playlistItem = event.details.playlistItem;
        const metadata = (playlistItem && playlistItem.metadata) || {};
        if (metadata.contentType === METADATA_TYPE_STREAM) {
          logger.log(`Tracking: progress:${event.details.position}s`, 'debug1');
        } else {
          logger.log(`Tracking: progress:${event.details.percent}%`, 'debug1');
        }
      } else {
        logger.log(`Tracking: ${event.details.event}`, 'debug1');
      }
    } else {
      logger.log(`Event: ${event.type}`, 'debug' + ((event.type !== 'timeupdate' && event.type !== 'adtimeupdate') ? 1 : 2));
    }

    // default the event uid to null
    event.uid = null;

    // as long as the event type does not appear in the list, update its uid
    // to reflect the uid of the playlist item currently selected
    if (event.type !== EVENT_PLAYLIST_CHANGE) {
      // retrieve the metadata of the current playing item
      const meta = api.getMetadata();

      // add the current playing content uid to the event
      event.uid = (meta && meta.uid) || null;
    }

    const result = EventEmitter.prototype.emit.call(events, event);

    // trigger postMessage
    if (window.parent) {
      try {
        window.parent.postMessage(Object.assign({}, event, { type: `iono:player:${event.type}` }), '*');
      } catch (e) {
        Logger.error(`postMessage error: ${e.message}`, e);
      }
    }

    return result;
  };

  // helper functions for the state machine

  // begin playback
  const contentPlay = function (ctx, event) {
    ctx.paused = ctx.content.paused = false;
    _primePlayer(ctx);
    if (ctx.beforePlay) {
      ctx.beforePlay = false;
      if (advertising) {
        this.transition(STATE_WAITING, event);
        ctx.prerollPromise = advertising.playPreroll()
          .then(function (hasPrerolls) {
            if (!hasPrerolls) content.play();
          })
          .catch(e => {
            content.play();
          });

        return ctx.prerollPromise;
      }
    }

    content.play();
  };

  // pause current playback
  const contentPause = function (ctx, event) {
    ctx.paused = ctx.content.paused = true;
    content.pause();
  };

  // default handler for content play events
  const contentPlayed = function (ctx, event) {
    ctx.paused = ctx.content.paused = false;
    events.emitAsync(EVENT_PLAY);
  };

  // default handler for content pause events
  const contentPaused = function (ctx, event) {
    if (content.isLive()) {
      // clear and reinsert timeline entries
      ctx.timeline.clear();
      if (advertising) {
        advertising._scheduleAdBreaks();
      }
    }
    ctx.paused = ctx.content.paused = true;
    events.emitAsync(EVENT_PAUSE);
    this.transition(content.isLive() ? STATE_IDLE : STATE_PAUSED);
  };

  // default handler for content resume events
  const contentResumed = function (ctx, event) {
    ctx.paused = ctx.content.paused = false;
    events.emitAsync(EVENT_RESUME);
  };

  // handler for content tracking events
  const contentTracking = function (ctx, event) {
    const apiEvent = {
      type: EVENT_TRACKING,
      details: {
        event: event.context.event,
        contentType: event.context.contentType,
        uid: event.context.uid,
        position: event.context.position,
        interrupted: event.details.interrupted || false
      }
    };

    if (typeof event.details.action !== 'undefined') {
      apiEvent.details.action = event.details.action;
    }

    if (typeof event.context.percent !== 'undefined') {
      apiEvent.details.percent = event.context.percent;
    }

    events.emitAsync(apiEvent);
  };

  // handler for content stop events
  const contentStopped = function (ctx, event) {
    if (content.isLive()) {
      // clear and reinsert timeline entries
      ctx.timeline.clear();
      if (advertising) {
        advertising._scheduleAdBreaks();
      }
    }
    ctx.paused = ctx.content.paused = true;
    events.emitAsync(EVENT_STOP);
  };

  // handler for duration changed events
  const contentDurationChanged = function (ctx, event) {
    ctx.duration = ctx.content.duration = content.getDuration();
    ctx.timeline.setDuration(ctx.duration);

    events.emitAsync({
      type: EVENT_DURATION_CHANGE,
      duration: ctx.duration
    });
  };

  // handler for timeupdate events
  const contentTimeUpdated = function (ctx, event) {
    ctx.position = content.getPosition();
    ctx.content.position = content.getPosition();
    ctx.timeline.setPosition(ctx.position);

    events.emitAsync({
      type: EVENT_POSITION,
      position: ctx.content.position
    });

    // trigger the buffer progress if it has changed
    const bufferedPercentage = content.getBufferedPercentage();
    if (ctx.bufferedPercentage !== bufferedPercentage) {
      this.send({
        type: ContentEvents.Progress,
        buffered: content.getBuffered(),
        bufferedPercentage: bufferedPercentage
      });
    }
  };

  // handler for playback rate changed events
  const contentRateChanged = function (ctx, event) {
    if (ctx.playbackRate === event.playbackRate) return;
    ctx.playbackRate = event.playbackRate;
    events.emitAsync({
      type: EVENT_PLAYBACK_RATE_CHANGE,
      playbackRate: ctx.playbackRate
    });
  };

  // handler for when playlist changes
  const contentPlaylistChanged = function (ctx, event) {
    ctx.playlist = content.playlist.getPlaylist();
    ctx.playlistIndex = content.playlist.getPlaylistIndex();
    ctx.playlistItem = content.playlist.getPlaylistItem();
    ctx.playlistSkip = ctx.playlist.length;

    // set the playlist url to the url in playlist metadata
    const playlistMetadata = content.playlist.getPlaylistMetadata();
    if (playlistMetadata.url && playlistMetadata.url.match(/<(skip|limit)>/) !== null) {
      ctx.playlistUrl = playlistMetadata.url;
    } else if (ctx.playlistUrl && ctx.playlistUrl.match(/<(skip|limit)>/) === null) {
      ctx.playlistUrl = null;
    }

    if (
      !ctx.playlistUrl ||
      (event.added > 0 && event.added !== ctx.playlistLimit) ||
      (event.loaded > 0 && event.loaded !== ctx.playlistLimit) ||
      (event.loaded === 0 && event.added === 0)
    ) {
      ctx.canLoadMore = false;
    } else {
      ctx.canLoadMore = true;
    }

    events.emitAsync({
      ...event,
      type: EVENT_PLAYLIST_CHANGE
    });
  };

  const contentSeeking = function (ctx, event) {
    events.emitAsync({
      type: EVENT_SEEK,
      uid: null,
      position: 0
    });
  };

  const contentSeeked = function (ctx, event) {
    events.emitAsync({
      type: EVENT_SEEKED,
      uid: null,
      position: 0
    });
  };

  const contentSeekable = function (ctx, event) {
    if (content.isSeekable()) {
      ctx.isSeekable = true;
      events.emitAsync({
        type: EVENT_SEEKABLE,
        seekable: content.getSeekable()
      });
    }
  };

  // default handler for playlist select events
  const playlistSelected = function (ctx, event) {
    ctx.duration = ctx.content.duration = content.getDuration();
    ctx.playlistItem = content.playlist.getPlaylistItem();
    ctx.playlistIndex = content.playlist.getPlaylistIndex();
    ctx.beforePlay = true;

    // load more items when we reach the last item in the playlist
    if (api.canLoadMore() && ctx.playlistIndex === content.playlist.getPlaylist().length - 1) {
      api.loadMore();
    }

    this.transition(STATE_IDLE, event);

    events.emitAsync({
      type: EVENT_PLAYLIST_ITEM_CHANGE,
      playlistIndex: ctx.playlistIndex,
      playlistItem: ctx.playlistItem
    });
  };

  // update the list of available text tracks
  const updateTextTracks = function (ctx, event) {
    ctx.textTracks = content.textTracks.getTextTracks()
      .reduce(function (tracks, track) {
        if (track.mode === 'hidden') {
          return tracks;
        }

        if (
          (track.kind === 'chapters') ||
          (track.kind === 'subtitles') ||
          (track.kind === 'captions')
        ) {
          tracks.push(createTextTrack(track));
        }

        return tracks;
      }, []);

    updateLanguages.call(this, ctx, event);
    updateChapters.call(this, ctx, event);
    updateCaptions.call(this, ctx, event);
    updateSubtitles.call(this, ctx, event);

    if (this.state !== STATE_EMPTY && this.state !== STATE_NOT_READY) {
      events.emitAsync({
        type: EVENT_TRACKS_CHANGE,
        tracks: ctx.textTracks
      });
    }
  };

  // update the list of available languages
  const updateLanguages = function (ctx, event) {
    const tracks = api.getTextTracks();

    // update available languages
    const languages = tracks.reduce(function (languages, track) {
      if (languages.indexOf(track.language) === -1) {
        languages.push(track.language);
      }
      return languages;
    }, []);

    if (ctx.languages.join('.') !== languages.join('.')) {
      ctx.languages = languages;
      events.emitAsync({
        type: EVENT_LANGUAGES_CHANGE,
        languages: ctx.languages
      });
    }
  };

  // update the list of available chapters
  const updateChapters = function (ctx, event) {
    const chapterTracks = content.textTracks.getTextTracks()
      .filter(function (track) {
        return track.kind === 'chapters';
      });

    if (chapterTracks && chapterTracks[0]) {
      ctx.chapters = chapterTracks[0].cues.map(createTextTrackCue);
    } else {
      ctx.chapters = [];
    }
  };

  // update the list of available captions
  const updateCaptions = function (ctx, event) {
    ctx.captions = api.getTextTracks('captions');
  };

  // update the list of available subtitles
  const updateSubtitles = function (ctx, event) {
    ctx.subtitles = api.getTextTracks('subtitles');
  };

  // update the modes of current text tracks
  const updateTextTrackModes = function (ctx, event) {
    const language = settings.get('language');
    const tracks = {};

    content.textTracks.getTextTracks()
      .forEach(track => {
        if (
          (track.kind === 'captions') ||
          (track.kind === 'subtitles') ||
          (track.kind === 'chapters')
        ) {
          // If the track is in a different language, or a track of this kind has
          // already been shown, then disable the current track
          if (track.language === language && !tracks[track.kind]) {
            tracks[track.kind] = track.id;
            content.textTracks.setTextTrackMode(track, 'showing');
          } else {
            content.textTracks.setTextTrackMode(track, 'disabled');
          }
        }
      });
  };

  // update the current playback source
  const contentSourceChanged = function (ctx, event) {
    ctx.source = event.source;
    events.emitAsync({
      type: EVENT_SOURCE_CHANGE,
      source: ctx.source
    });
  };

  // update the chosen quality level
  const levelChanged = function (ctx, event) {
    ctx.qualityLevel = createQualityLevel(event.level);
    ctx.quality = ctx.qualityLevel.label;
    events.emitAsync({
      type: EVENT_LEVEL_CHANGE,
      level: ctx.qualityLevel
    });
  };

  // update the current quality levels
  const levelsChanged = function (ctx, event) {
    ctx.qualityLevels = event.levels.map(createQualityLevel);
    ctx.qualityLevel = event.level ? createQualityLevel(event.level) : false;
    events.emitAsync({
      type: EVENT_LEVELS_CHANGE,
      levels: ctx.qualityLevels,
      level: ctx.qualityLevel
    });
  };

  // update the current buffered context
  const contentProgressChanged = function (ctx, event) {
    if (ctx.bufferedPercentage !== event.bufferedPercentage) {
      ctx.buffered = event.buffered;
      ctx.bufferedPercentage = event.bufferedPercentage;
      events.emitAsync({
        type: EVENT_PROGRESS,
        buffered: ctx.buffered,
        bufferedPercentage: ctx.bufferedPercentage
      });
    }
  };

  // update player state when content playback finishes
  const contentEnded = function (ctx, event) {
    const playlistItem = ctx.playlistItem;
    const metadata = playlistItem.metadata || {};

    // prevent emitting complete events on live streams
    // @todo should check isLive instead of content type
    if (metadata.contentType === METADATA_TYPE_STREAM) {
      return;
    }

    // play advertising postroll
    if (advertising) {
      // wait for in-stream postrolls to complete before moving to next playlist item
      if (advertising.inAdBreak()) {
        advertising.one(EVENT_AD_BREAK_END, _ => {
          playNextPlaylistItemAfterEnded.call(this, ctx, event);
        });
        return;
      }

      // try start the postroll, the ad manager returns a promise that always resolves
      // the value of the resolved promise is a boolean used to check if a postroll should play
      advertising.startPostroll()
        .then(hasPostroll => {
          if (hasPostroll) {
            advertising.one(EVENT_AD_BREAK_END, _ => {
              playNextPlaylistItemAfterEnded.call(this, ctx, event);
            });
          } else {
            playNextPlaylistItemAfterEnded.call(this, ctx, event);
          }
        }, _ => {
          playNextPlaylistItemAfterEnded.call(this, ctx, event);
        });
    } else {
      playNextPlaylistItemAfterEnded.call(this, ctx, event);
    }
  };

  const playNextPlaylistItemAfterEnded = function (ctx, event) {
    ctx.paused = ctx.content.paused = true;
    ctx.position = ctx.content.position = 0;
    this.transition(STATE_PAUSED);
    events.emitAsync(EVENT_COMPLETE);

    // repeat the current source if the repeat mode is set to single
    if (settings.get('repeat') === REPEAT_MODE_SINGLE) {
      api.setPosition(0);
      return api.play();
    }

    const currentIndex = api.getPlaylistIndex();
    const lastIndex = api.getPlaylist().length - 1;

    // repeat the entire playlist from the start
    if (settings.get('repeat') === REPEAT_MODE_PLAYLIST && currentIndex >= lastIndex) {
      api.selectPlaylistItem(0);
      return api.play();
    }

    if (currentIndex < lastIndex) {
      api.nextPlaylistItem();
      return api.play();
    }
  };

  /**
   * Get playlist url and options
   *
   * @todo deprecate playlistSourceAndOptions, the source loader does the job this used to do
   *
   * @param  {string|array|object} source
   * @param  {object} options
   * @return {object}
   */
  const playlistSourceAndOptions = function (source, options) {
    options = Object.assign({
      skip: 0,
      limit: 20
    }, options);

    let playlistUrl = null;

    if (isString(source)) {
      playlistUrl = source;
    }

    return {
      url: playlistUrl,
      source,
      options
    };
  };

  // build the state machine
  const SM = Object.assign(new EventEmitter(), {
    // the context is passed as the first parameter to all event handlers in the state machine
    // context is passed on to the next state after a transition
    context: {
      ready: false,
      loading: false,
      paused: true,
      error: false,
      position: 0,
      duration: 0,
      preload: options.preload,
      autoplay: options.autoplay,
      playlist: false,
      playlistItem: false,
      playlistIndex: false,
      playOnLoad: false,
      quality: options.qualityLevel,
      qualityLevels: [],
      source: false,
      volume: options.volume,
      playbackRate: options.playbackRate,
      isLive: false,
      isMute: false,
      isSeekable: false,
      buffered: [],
      bufferedPercentage: 0,
      repeat: options.repeat,

      playbackReady: false,

      language: options.language,
      languages: [],
      textTracks: [],
      captions: [],
      activeCaptions: [],
      subtitles: [],
      activeSubtitles: [],
      chapters: [],
      activeChapter: false,

      inAdBreak: false,
      currentAd: false,

      content: {
        position: 0,
        duration: 0,
        paused: true
      },
      advert: {
        position: 0,
        duration: 0,
        paused: true
      },
      timeline: _timeline
    },

    // default event handlers, if a handler is not found in the current state
    // one of these handlers will be executed
    events: {
      [ApiActions.Add]: readyFn(function _addWhenReady (ctx, event) {
        content.add(event.source, event.options);
      }),
      [ApiActions.LoadMore] (ctx, event) {
        // ignore when a playlist url is not present on current context
        if (!ctx.canLoadMore) return;

        // prevent loading more items while the player is already loading
        if (content._addingPlaylistItems) {
          return;
        }

        // gather the options, defaulting skip to the current skip + current limit
        // so that we get the next "page" of playlist items
        const options = Object.assign({
          skip: ctx.playlistSkip,
          limit: ctx.playlistLimit
        }, event.options);

        const sourceAndOptions = playlistSourceAndOptions(ctx.playlistUrl, options);

        content.add(sourceAndOptions.source, sourceAndOptions.options);
      },
      [ApiActions.Load]: readyFn(function _loadWhenReady (ctx, event) {
        this.transition(STATE_EMPTY, event);
        this.send(event);
      }),
      [ApiActions.Unload]: readyFn(function _unloadWhenReady (ctx, event) {
        content.unload();
        advertising && advertising.unload();
        events.emitAsync(EVENT_UNLOAD);
        this.transition(STATE_EMPTY, event);
      }),
      [ApiActions.Mute] (ctx, event) {
        ctx.isMute = true;
        content.setMute(true);
        // @todo set muted on advertising player
      },
      [ApiActions.Unmute] (ctx, event) {
        ctx.isMute = false;
        content.setMute(false);
        // @todo unset muted on advertising player
      },
      [ApiActions.SetPlaybackRate] (ctx, event) {
        content.setPlaybackRate(event.playbackRate);
      },
      [ApiActions.SetQualityLevel] (ctx, event) {
        ctx.quality = event.level;
        content.levels.setQualityLevel(event.level);
      },
      [ApiActions.SetVolume] (ctx, event) {
        ctx.volume = event.volume;
        content.setVolume(event.volume);
        // @todo set volume on advertising player
      },
      [ContentEvents.VolumeChange] (ctx, event) {
        ctx.volume = event.volume;
        ctx.isMute = event.muted;
        events.emitAsync({
          type: EVENT_VOLUME_CHANGE,
          volume: ctx.volume,
          muted: ctx.isMute
        });
      },
      [ContentEvents.LanguagesChange] (ctx, event) {
        ctx.languages = event.languages;
      },
      [ContentEvents.TextTrackCueChange] (ctx, event) {
        const track = createTextTrack(event.track);
        const cues = event.currentCues.map(createTextTrackCue);

        switch (track.kind) {
          case 'chapters':
            ctx.activeChapter = cues[0] || false;
            break;
          case 'captions':
            ctx.activeCaptions = cues;
            break;
          case 'subtitles':
            ctx.activeSubtitles = cues;
            break;
          default: return;
        }

        events.emitAsync({
          type: EVENT_CUE_CHANGE,
          track: track,
          currentCues: cues
        });
      },
      [ContentEvents.LevelChange]: levelChanged,
      [ContentEvents.Loading]: STATE_LOADING,
      [ContentEvents.PlaylistSelect]: playlistSelected,
      [ContentEvents.PlaylistChange]: contentPlaylistChanged,
      [ContentEvents.TextTracksChange]: updateTextTracks,
      [ContentEvents.SourceChange]: contentSourceChanged,
      [ContentEvents.RateChange]: contentRateChanged,
      [ContentEvents.DurationChange]: contentDurationChanged,
      [ContentEvents.Progress]: contentProgressChanged,
      [ContentEvents.Error] (ctx, event) {
        ctx.error = event.error instanceof PlayerError ? event.error : PlayerError.fromMediaError(event.error);
        ctx.content.paused = ctx.paused = true;
        this.transition(STATE_ERROR);
      },
      [ContentEvents.PlaylistLoad] (ctx, event) {
        events.emitAsync({
          ...event,
          type: EVENT_PLAYLIST_LOAD
        });
      },
      [ContentEvents.Ended]: contentEnded,
      [ContentEvents.NowPlayingChange] (ctx, event) {
        // now playing data only available on live sources and when the entry contentType is 'metadata'
        if (!api.isLive()) {
          return;
        }

        if ((event.metadata && [METADATA_TYPE_NOWPLAYING, METADATA_TYPE_METADATA].includes(event.metadata.contentType)) || event.metadata === null) {
          events.emitAsync({
            type: EVENT_NOWPLAYING_CHANGE,
            metadata: event.metadata
          });
        }
      },
      [ContentEvents.Stop]: contentStopped,
      [ContentEvents.Destroy] (ctx, event) {
        this.transition(STATE_NOT_READY);
        events.emitAsync({
          type: EVENT_DESTROY
        });
      },
      [ContentEvents.CanPlay] (ctx, event) {
        if (!ctx.canPlay) {
          ctx.canPlay = true;
          events.emitAsync({
            type: EVENT_CANPLAY
          });

          if (!ctx.isSeekable) contentSeekable(ctx, event);
        }
      }
    },

    // holds the current state name and serves to tell the machine it's current state
    state: STATE_NOT_READY,

    // defines the mapping of states and their event / action handlers
    states: {
      [STATE_NOT_READY]: {
        // the onEnter hook is called when transitioning in to a state
        onEnter (ctx) {
          ctx.ready = false;
        },
        // the onExit hook is called when transitioning out of a state
        onExit (ctx) {
          ctx.ready = true;
          setTimeout(function _flushReadyQueueTimeout () {
            readyQueue.forEach(function _flushReadyQueue (callback) {
              callback.call(api);
            });
            events.emitAsync(EVENT_READY);
          }, 0);
        },
        events: {
          [ApiActions.Play]: readyFn(function (ctx, event) {
            SM.send(event);
          }),
          [ApiActions.Pause]: readyFn(function (ctx, event) {
            SM.send(event);
          })
        }
      },
      [STATE_EMPTY]: {
        onEnter (ctx, event) {
          ctx.paused = ctx.content.paused = true;
          ctx.playlist = false;
          ctx.playlistIndex = false;
          ctx.playlistItem = false;
          ctx.position = ctx.content.position = content.getPosition();
          ctx.duration = ctx.content.duration = content.getDuration();
          ctx.content.canPlay = false;
          ctx.source = content.getSource();
          ctx.qualityLevel = false;
          ctx.qualityLevels = [];
          ctx.isSeekable = false;
          updateTextTracks.call(this, ctx, event);
          events.emitAsync(EVENT_EMPTY);
        },
        events: {
          [ApiActions.Load] (ctx, event) {
            const sourceAndOptions = playlistSourceAndOptions(event.source, event.options);

            ctx.playlistUrl = sourceAndOptions.url;
            ctx.playlistLimit = sourceAndOptions.options.limit;
            ctx.playlistSkip = sourceAndOptions.options.skip;

            content.load(sourceAndOptions.source, sourceAndOptions.options);
          }
        }
      },
      [STATE_LOADING]: {
        onEnter (ctx, event) {
          ctx.loading = true;
          ctx.paused = ctx.content.paused = true;
          ctx.playlist = false;
          ctx.playlistIndex = false;
          ctx.playlistItem = false;
          ctx.position = ctx.content.position = content.getPosition();
          ctx.duration = ctx.content.duration = content.getDuration();
          ctx.content.canPlay = false;
          ctx.source = content.getSource();
          ctx.playOnLoad = false;
          ctx.beforePlay = true;
          ctx.isSeekable = false;
          ctx.timeline.clear();
          events.emitAsync(EVENT_LOADING);
        },
        onExit (ctx) {
          ctx.loading = false;
        },
        events: {
          // [ContentEvents.PlaylistLoad] (ctx, event) {
          //   events.emitAsync({
          //     ...event,
          //     type: EVENT_PLAYLIST_LOAD
          //   });
          // },
          [ApiActions.Play] (ctx) {
            _primePlayer(ctx);
            ctx.playOnLoad = true;
          },
          [ApiActions.Pause] (ctx) {
            ctx.playOnLoad = false;
          },
          [ApiActions.Stop] (ctx) {
            ctx.playOnLoad = false;
          },
          [ContentEvents.Resume]: contentResumed,
          [ContentEvents.Loaded]: STATE_IDLE,
          [ContentEvents.LevelsChange]: levelsChanged,
          [ContentEvents.Seeking]: contentSeeking,
          [ContentEvents.Seeked]: contentSeeked
        }
      },
      [STATE_IDLE]: {
        onEnter (ctx, event) {
          ctx.source = content.getSource();
          ctx.paused = ctx.content.paused = true;
          ctx.content.canPlay = false;
          ctx.isSeekable = false;

          if (ctx.playOnLoad) {
            ctx.playOnLoad = false;
            this.send(ApiActions.Play);
          }
        },
        events: {
          [ApiActions.Play]: contentPlay,
          [AdvertEvents.BreakStart]: STATE_ADPLAYING,
          [ContentEvents.Waiting]: STATE_WAITING,
          [ContentEvents.Tracking]: contentTracking,
          [ContentEvents.Resume]: contentResumed,
          [ContentEvents.LevelsChange]: levelsChanged,
          [ContentEvents.Play]: contentPlayed,
          [ContentEvents.Playing]: STATE_PLAYING,
          [ContentEvents.TimeUpdate]: contentTimeUpdated,
          [ContentEvents.LoadStart]: STATE_WAITING,
          [ContentEvents.Seeking]: contentSeeking,
          [ContentEvents.Seeked]: contentSeeked,
          [ContentEvents.PlaylistLoad] (ctx, event) {
            if (event.mode !== 'add') {
              this.transition(STATE_LOADING, event);
            }
            events.emitAsync({
              ...event,
              type: EVENT_PLAYLIST_LOAD
            });
          }
        }
      },
      [STATE_WAITING]: {
        onEnter (ctx, event) {
          events.emitAsync(EVENT_WAITING);
        },
        events: {
          [ApiActions.Play]: function (ctx, event) {
            if (!inAdBreak()) {
              contentPlay.call(this, ctx, event);
            }
          },
          [ApiActions.Pause]: function (ctx, event) {
            if (!inAdBreak()) {
              contentPause.call(this, ctx, event);
            }
          },
          [ApiActions.Stop] (ctx, event) {
            if (!inAdBreak()) {
              this.transition(content.isLive() ? STATE_IDLE : STATE_PAUSED, event);
              events.emitAsync(EVENT_STOP);
            }
          },
          [ApiActions.SetPosition] (ctx, event) {
            // if (!inAdBreak()) {
            content.setPosition(event.position);
            // }
          },
          [ContentEvents.Error] (ctx, event) {
            ctx.error = event.error;
            this.transition(STATE_ERROR, event);
          },
          [ContentEvents.Tracking]: contentTracking,
          [ContentEvents.Stop]: contentStopped,
          [ContentEvents.Play]: contentPlayed,
          [ContentEvents.Pause]: contentPaused,
          [ContentEvents.Resume]: contentResumed,
          [ContentEvents.TimeUpdate]: contentTimeUpdated,
          [ContentEvents.Playing]: STATE_PLAYING,
          [ContentEvents.Paused]: STATE_PAUSED,
          [ContentEvents.Seeking]: contentSeeking,
          [ContentEvents.Seeked]: contentSeeked,
          [AdvertEvents.BreakStart]: STATE_ADPLAYING
        }
      },
      [STATE_PLAYING]: {
        onEnter (ctx) {
          ctx.paused = ctx.content.paused = false;
          ctx.content.canPlay = true;
          if (!ctx.isSeekable) contentSeekable(ctx);
          events.emitAsync(EVENT_PLAYING);
        },
        events: {
          [ApiActions.Pause]: contentPause,
          [ApiActions.Stop] (ctx, event) {
            content.stop();
          },
          [ApiActions.SetPosition] (ctx, event) {
            content.setPosition(event.position);
          },
          [AdvertEvents.BreakStart]: STATE_ADPLAYING,
          [ContentEvents.Stop] (ctx, event) {
            ctx.paused = ctx.content.paused = true;
            events.emitAsync(EVENT_STOP);
          },
          [ContentEvents.Tracking]: contentTracking,
          [ContentEvents.TimeUpdate]: contentTimeUpdated,
          [ContentEvents.Waiting]: STATE_WAITING,
          [ContentEvents.Ended]: contentEnded,
          [ContentEvents.Pause]: contentPaused,
          [ContentEvents.Paused] (ctx, event) {
            this.transition(content.isLive() ? STATE_IDLE : STATE_PAUSED, event);
          },
          [ContentEvents.Seeking]: contentSeeking,
          [ContentEvents.Seeked]: contentSeeked
        }
      },
      [STATE_PAUSED]: {
        onEnter (ctx) {
          ctx.paused = ctx.content.paused = true;
          ctx.content.canPlay = true;
          this.emitAsync(EVENT_PAUSED);
        },
        events: {
          [ApiActions.Play]: contentPlay,
          [ApiActions.Stop] (ctx, event) {
            content.stop();
          },
          [ApiActions.SetPosition] (ctx, event) {
            content.setPosition(event.position);
          },
          [AdvertEvents.BreakStart]: STATE_ADPLAYING,
          [ContentEvents.Tracking]: contentTracking,
          [ContentEvents.TimeUpdate]: contentTimeUpdated,
          [ContentEvents.Stop] (ctx, event) {
            ctx.paused = ctx.content.paused = true;
            this.transition(content.isLive() ? STATE_IDLE : STATE_PAUSED, event);
            events.emitAsync(EVENT_STOP);
          },
          [ContentEvents.Play] (ctx, event) {
            ctx.paused = ctx.content.paused = false;
            events.emitAsync(EVENT_PLAY);
          },
          [ContentEvents.Resume]: contentResumed,
          [ContentEvents.Playing]: STATE_PLAYING,
          [ContentEvents.Seeking]: contentSeeking,
          [ContentEvents.Seeked]: contentSeeked
        }
      },
      [STATE_ADPLAYING]: {
        onEnter (ctx) {
          ctx.inAdBreak = true;

          events.emitAsync({
            type: EVENT_AD_BREAK_START
          });
        },
        onExit (ctx) {
          ctx.inAdBreak = false;

          events.emitAsync({
            type: EVENT_AD_BREAK_END
          });
        },
        events: {
          // [ApiActions.Play] (ctx, event) {
          //   advertising.play();
          // },
          // [ApiActions.Pause] (ctx, event) {
          //   advertising.pause();
          // },
          [AdvertEvents.BreakEnd] (ctx, event) {
            this.transition(content.getState());
          },
          [AdvertEvents.Start] (ctx, event) {
            settings.set('lastAdRollTime', new Date());
            settings.save();

            events.emitAsync({
              type: EVENT_AD_START,
              ad: advertising.getCurrentAd(),
              position: content.getPosition()
            });
          },
          [AdvertEvents.End] (ctx, event) {
            ctx.advert.paused = true;
            ctx.advert.position = 0;
            ctx.advert.duration = 0;
            events.emitAsync({
              type: EVENT_AD_END,
              ad: advertising.getCurrentAd()
            });
          },
          [ApiActions.Stop] (ctx, event) {
            advertising.stop();
          },
          [ApiActions.Skip] (ctx, event) {
            advertising.skip();
          },
          [AdvertEvents.Playing] (ctx, event) {
            events.emitAsync({
              type: `ad${EVENT_PLAYING}`,
              ad: advertising.getCurrentAd()
            });
          },
          [AdvertEvents.Tracking] (ctx, event) {
            if (typeof event.details.aduid === 'undefined') {
              return contentTracking(ctx, event);
            }

            const playlistItem = api.getCurrentAd();
            const metadata = (playlistItem && playlistItem.metadata) || {};
            const apiEvent = {
              type: EVENT_TRACKING,
              details: {
                event: event.details.event,
                contentType: metadata.contentType,
                uid: metadata.uid,
                position: event.details.position,
                interrupted: metadata.interrupted || false
              }
            };

            if (typeof event.details.action !== 'undefined') {
              apiEvent.details.action = event.details.action;
            }

            if (typeof event.details.percent !== 'undefined') {
              apiEvent.details.percent = event.details.percent;
            }

            events.emitAsync(apiEvent);
          },
          // [AdvertEvents.Waiting] (ctx, event) {
          //   events.emitAsync({
          //     type: `ad${EVENT_WAITING}`,
          //     ad: advertising.getCurrentAd()
          //   });
          // },
          [AdvertEvents.Play] (ctx, event) {
            ctx.paused = ctx.advert.paused = false;
          },
          [AdvertEvents.Pause] (ctx, event) {
            ctx.paused = ctx.advert.paused = true;
          },
          [AdvertEvents.Resume] (ctx, event) {
            ctx.paused = ctx.advert.paused = false;
            // events.emitAsync(`ad${EVENT_RESUME}`);
          },
          [AdvertEvents.DurationChange] (ctx, event) {
            ctx.advert.duration = event.duration;
            events.emitAsync({
              type: `ad${EVENT_DURATION_CHANGE}`,
              duration: event.duration,
              ad: advertising.getCurrentAd()
            });
          },
          [AdvertEvents.TimeUpdate] (ctx, event) {
            ctx.advert.position = event.position;
            events.emitAsync({
              type: `ad${EVENT_POSITION}`,
              ad: advertising.getCurrentAd(),
              position: event.position
            });
          },
          // [AdvertEvents.PlaylistLoad] (ctx, event) {
          //   events.emitAsync(`ad${EVENT_PLAYLIST_LOAD}`);
          // },
          // [AdvertEvents.PlaylistSelect] (ctx, event) {
          //   events.emitAsync({
          //     type: `ad${EVENT_PLAYLIST_ITEM_CHANGE}`,
          //     playlistIndex: event.playlistIndex,
          //     playlistItem: event.playlistItem
          //   });
          // },
          [AdvertEvents.Skip] (ctx, event) {
            events.emitAsync({
              type: EVENT_AD_SKIP,
              ad: advertising.getCurrentAd(),
              position: advertising.getPosition()
            });
          },
          [AdvertEvents.Skippable] (ctx, event) {
            events.emitAsync({
              type: EVENT_AD_SKIPPABLE,
              ad: advertising.getCurrentAd()
            });
          },
          [AdvertEvents.CompanionStart] (ctx, event) {
            const companion = event.companion;
            companion.url = computed.replace(companion.url, encodeURIComponent, event);
            if (companion.clickthrough) {
              companion.clickthrough = computed.replace(companion.clickthrough, encodeURIComponent, event);
            }

            events.emitAsync({
              type: EVENT_AD_COMPANION_START,
              ad: advertising.getCurrentAd(),
              companion: companion,
              position: advertising.getPosition()
            });
          },
          [AdvertEvents.CompanionEnd] (ctx, event) {
            const companion = event.companion;
            companion.url = computed.replace(companion.url, encodeURIComponent, event);
            if (companion.clickthrough) {
              companion.clickthrough = computed.replace(companion.clickthrough, encodeURIComponent, event);
            }

            events.emitAsync({
              type: EVENT_AD_COMPANION_END,
              ad: advertising.getCurrentAd(),
              companion: companion,
              position: advertising.getPosition()
            });
          },
          [ContentEvents.Play] (ctx, event) {
            if (advertising._isPlayingServerSideAd()) {
              contentPlayed(ctx, event);
            }
          },
          [ContentEvents.Tracking]: contentTracking,
          [ContentEvents.TimeUpdate]: contentTimeUpdated
        }
      },
      [STATE_ERROR]: {
        onEnter (ctx) {
          ctx.paused = ctx.content.paused = content.isPaused();
          events.emitAsync({
            type: EVENT_ERROR,
            error: ctx.error
          });
        },
        onExit (ctx) {
          ctx.error = false;
        },
        events: {
          [ApiActions.Play] (ctx, event) {
            if (!ctx.error.fatal) {
              content.play();
            }
          },
          [ContentEvents.SourceChange]: STATE_IDLE,
          [ContentEvents.LoadStart]: STATE_WAITING,
          [ContentEvents.Waiting]: STATE_WAITING
        }
      }
    },

    transition (newState, event) {
      return transition.call(api, this, newState, event);
    },

    send (event) {
      return send.call(api, this, event);
    }
  });

  // send nowplaying events in to the state machine
  _nowPlaying.on('change', function (event) {
    SM.send({
      type: ContentEvents.NowPlayingChange,
      metadata: event.metadata
    });
  });

  // check if the player is currently in an ad break
  function inAdBreak () {
    return advertising && advertising.inAdBreak();
  }

  // start of the public api...

  /**
   * Wraps a function in a ready check
   *
   * @param {function} fn
   */
  function readyFn (fn) {
    return function () {
      const args = arguments;
      const context = this;
      return api.ready(function _readyWrapper () {
        fn.apply(context, args);
      });
    };
  }

  //
  // SETTINGS
  //

  /**
   * Retrieve the current repeat mode
   *
   * @return {string}
   */
  this.getRepeat = function () {
    return SM.context.repeat;
  };

  /**
   * Modify the players GDPR configuration
   *
   * @param {boolean|object} gdpr
   */
  this.setGDPR = function (gdpr) {
    // Must be a boolean or object
    if (typeof gdpr !== 'boolean' && typeof gdpr !== 'object') {
      throw new TypeError(`Invalid GDPR value, expected a boolean or object but got '${gdpr}'`);
    }
    if (typeof gdpr === 'object' && typeof gdpr.userConsent !== 'string') {
      throw new TypeError(`Invalid user consent value, expected a string but got '${gdpr}'`);
    }
    this.options.gdpr = gdpr;
    if (_adswizz) {
      _adswizz.setGDPR(gdpr);
    }
  };

  /**
   * Change the current repeat mode
   *
   * @throws {TypeError} Argument mode must be a string
   * @throws {Error} Argument mode must be a valid repeat mode
   *
   * @param {string} mode the repeat mode to set
   */
  this.setRepeat = function (mode) {
    if (typeof mode !== 'string') {
      throw new TypeError(`Invalid value for 'mode', expected a string but got '${mode}'`);
    }
    if ([REPEAT_MODE_NONE, REPEAT_MODE_PLAYLIST, REPEAT_MODE_SINGLE].indexOf(mode) === -1) {
      throw new Error(`Invalid repeat mode '${mode}'`);
    }
    logger.log(`Action: setRepeat(${JSON.stringify(mode)})`, 'debug2');
    SM.context.repeat = mode;
    content.playlist.setRepeat(mode);
    events.emitAsync({
      type: EVENT_REPEAT_CHANGE,
      repeat: mode
    });
  };

  /**
   * Retrieve the current preload setting
   *
   * @return {boolean}
   */
  this.getPreload = function () {
    return SM.context.preload;
  };

  /**
   * Change the current preload setting
   *
   * @throws {TypeError} Argument preload must be a boolean
   *
   * @param {boolean} preload
   */
  this.setPreload = function (preload) {
    if (typeof preload !== 'boolean') {
      throw new TypeError(`Invalid value for 'preload', expected boolean but got '${preload}'`);
    }
    logger.log(`Action: setPreload(${JSON.stringify(preload)})`, 'debug2');
    SM.context.preload = preload;
    content.setPreload(preload);
  };

  /**
   * Retrieve current mute state
   *
   * @return {boolean}
   */
  this.getMute = function () {
    return SM.context.isMute;
  };

  /**
   * Mute or unmute playback
   *
   * @throws {TypeError} Argument muted must be a boolean
   *
   * @param {boolean} muted Whether or not to mute playback (default: true)
   */
  this.setMute = function (muted = true) {
    if (typeof muted !== 'boolean') {
      throw new TypeError(`Invalid value for 'muted', expected boolean but got '${muted}'`);
    }

    logger.log(`Action: setMute(${JSON.stringify(muted)})`, 'debug2');

    SM.send(muted ? ApiActions.Mute : ApiActions.Unmute);
  };

  /**
   * Retrieve the current player volume
   *
   * @return {number}
   */
  this.getVolume = function () {
    return SM.context.volume;
  };

  /**
   * Change the players playback volume
   *
   * @throws {TypeError} Argument volume must be a number
   * @throws {RangeError} Argument volume must be a number between 0.0 and 1.0
   *
   * @param {number} volume
   */
  this.setVolume = function (volume) {
    if (typeof volume !== 'number') {
      throw new TypeError(`Invalid value for 'volume', expected a number but got '${volume}'`);
    }

    if (volume > 1.0 || volume < 0.0) {
      throw new RangeError(`Invalid value for 'volume', expected a number between 0.0 and 1.0, but got '${volume}'`);
    }

    logger.log(`Action: setVolume(${JSON.stringify(volume)})`, 'debug2');

    if (volume > 0.0) {
      settings.set('volume', volume);
      settings.save();
    }

    SM.send({
      type: ApiActions.SetVolume,
      volume: volume
    });
  };

  /**
   * Retrieve the current playback rate
   *
   * @return {number}
   */
  this.getPlaybackRate = function () {
    return SM.context.playbackRate;
  };

  /**
   * Change the players playback rate
   *
   * @throws {TypeError} Argument rate must be a number
   * @throws {RangeError} Argument rate must be a number between 0.4 and 3.0
   *
   * @param {number} rate A playback rate between 0.4 and 3.0
   */
  this.setPlaybackRate = function (rate) {
    if (typeof rate !== 'number') {
      throw new TypeError(`Invalid value for 'rate', expected number but got '${rate}'`);
    }

    if (rate > 3.0 || rate < 0.4) {
      throw new RangeError(`Invalid value for 'rate', expected a number between 0.4 and 3.0, but got '${rate}'`);
    }

    logger.log(`Action: setPlaybackRate(${JSON.stringify(rate)})`, 'debug2');

    settings.set('playbackRate', rate);
    settings.save();

    SM.send({
      type: ApiActions.SetPlaybackRate,
      playbackRate: rate
    });
  };

  /**
   * Retrieve the current active language code
   *
   * @return {string} a valid ISO 639-1 language code
   */
  this.getLanguage = function () {
    return SM.context.language;
  };

  /**
   * Retrieve a list of available languages, built from the loaded caption text tracks
   *
   * @return {string[]} a list of valid ISO 639-1 language codes
   */
  this.getLanguages = function () {
    return SM.context.languages;
  };

  /**
   * Changes the active language of the player which is used to determine which captions and chapters to show, and to translate things such as error messages
   *
   * @throws {TypeError} Argument lang must be a string
   * @throws {Error} Argument lang must be a in the list of supported languages
   *
   * @param {string} language A valid ISO 639-1 language code
   */
  this.setLanguage = function (language) {
    // validate that the language is an actual string
    if (typeof language !== 'string') {
      throw new TypeError(`Invalid value for 'lang', expected a string but got '${language}'`);
    }
    // validate that the selected language is available
    if (this.getLanguages().indexOf(language) === -1) {
      throw new Error(`Unsupported language '${language}'`);
    }
    logger.log(`Action: setLanguage(${JSON.stringify(language)})`, 'debug2');
    // update the state machine context
    SM.context.language = language;
    updateTextTrackModes.call(SM, SM.context);
    events.emit({
      type: EVENT_LANGUAGE_CHANGE,
      language: this.getLanguage()
    });
  };

  /**
   * Retrieve the label of the currently selected quality level
   *
   * @return {string}
   */
  this.getQualityLevel = function () {
    return SM.context.quality;
  };

  /**
   * Return the list of available quality levels
   *
   * @return {object[]}
   */
  this.getQualityLevels = function () {
    return SM.context.qualityLevels;
  };

  /**
   * Change the current quality level
   *
   * @throws {TypeError} When the label argument is not a string
   *
   * @param {string} label The label of the quality level
   */
  this.setQualityLevel = function (label) {
    if (typeof label !== 'string') {
      throw new TypeError(`Invalid value for 'label', expected string but got '${label}'`);
    }

    logger.log(`Action: setQualityLevel(${JSON.stringify(label)})`, 'debug2');

    // update the preferred quality level
    settings.set('qualityLevel', label);
    settings.save();

    SM.send({
      type: ApiActions.SetQualityLevel,
      level: label
    });
  };

  //
  // PLAYBACK
  //

  /**
   * Unloads the current source from playback
   */
  this.unload = function () {
    logger.log('Action: unload()', 'debug2');
    SM.send(ApiActions.Unload);
  };

  /**
   * Start playback of the current source
   */
  this.play = function () {
    logger.log('Action: play()', 'debug2');
    SM.send(ApiActions.Play);
  };

  /**
   * Pause playback of the current source
   */
  this.pause = function () {
    logger.log('Action: pause()', 'debug2');
    SM.send(ApiActions.Pause);
  };

  this.stop = function () {
    logger.log('Action: stop()', 'debug2');
    SM.send(ApiActions.Stop);
  };

  /**
   * Play or pause playback
   */
  this.togglePlay = function () {
    logger.log('Action: togglePlay()', 'debug2');
    return SM.send(SM.context.paused ? ApiActions.Play : ApiActions.Pause);
  };

  /**
   * Mute or unmute playback
   */
  this.toggleMute = function () {
    logger.log('Action: toggleMute()', 'debug2');
    return SM.send(content.getMute() ? ApiActions.Unmute : ApiActions.Mute);
  };

  /**
   * Retrieve the paused status of the player
   *
   * @return {boolean}
   */
  this.isPaused = function () {
    return SM.context.paused;
  };

  /**
   * Retrieve whether or not content can be seeked
   *
   * @returns {boolean}
   */
  this.isSeekable = function () {
    return SM.context.isSeekable;
  };

  /**
   * Retrieve the duration of the currently loaded playback source
   *
   * @return {number}
   */
  this.getDuration = function () {
    return content.getDuration();
  };

  /**
   * Retrieve the current playback position
   *
   * @return {number}
   */
  this.getPosition = function () {
    return content.getPosition();
  };

  /**
   * Seek to a new position in playback
   *
   * @param {number} seconds
   */
  this.setPosition = function (position) {
    logger.log(`Action: setPosition(${JSON.stringify(position)})`, 'debug2');
    return SM.send({
      type: ApiActions.SetPosition,
      position: position
    });
  };

  /**
   * Retrieve the time ranges currently seekable by the player
   *
   * @returns {array}
   */
  this.getSeekable = function () {
    return content.getSeekable();
  };

  /**
   * Retrieve the time ranges currently buffered by the player
   *
   * @return {array}
   */
  this.getBuffered = function () {
    return SM.context.buffered;
  };

  /**
   * Retrieve the percentage currently buffered by the player
   *
   * @return {number}
   */
  this.getBufferedPercentage = function () {
    return content.getBufferedPercentage();
  };

  /**
   * Retrieve the MediaElement instance attached to the player
   *
   * @returns {MediaElement}
   */
  this.getMedia = function () {
    return content.getMedia();
  };

  //
  // PLAYLIST
  //

  /**
   * Load a new playlist for playback
   *
   * @param {string|object|object[]} source  The source to be loaded
   * @param {object}                 options Extra options to pass to the loader
   */
  this.load = function (source, options = {}) {
    logger.log('Action: load()', { source, options }, 'debug2');
    SM.send({
      type: ApiActions.Load,
      source: source,
      options: options
    });
  };

  /**
   * Load more items into the playlist from the current playlist url
   *
   * @param  {Object} options Extra options to pass to the loader
   */
  this.loadMore = function (options = {}) {
    logger.log('Action: loadMore()', { options }, 'debug');
    SM.send({
      type: ApiActions.LoadMore,
      options: options
    });
  };

  /**
   * Determine if the player is able to load more playlist items in it's current configuration
   *
   * @return {boolean}
   */
  this.canLoadMore = function () {
    return !!SM.context.canLoadMore;
  };

  /**
   * Adds a new source to the current playback playlist
   *
   * @param {string|object|object[]} source  The source to be loaded
   * @param {object}                 options Extra options to pass to the loader
   */
  this.add = function (source, options = {}) {
    if (isString(source)) {
      source = computed.replace(source, encodeURIComponent);
    }
    logger.log('Action: add()', { source, options }, 'debug2');
    SM.send({
      type: ApiActions.Add,
      source: source,
      options: options
    });
  };

  /**
   * Retrieve the currently loaded playlist
   *
   * @return {object[]}
   */
  this.getPlaylist = function (asObject) {
    return content.playlist.getPlaylist(asObject);
  };

  /**
   * Retrieve a playlist item if an index is supplied, otherwise returns the current playlist item
   *
   * @param {number} index The index of the playlist item
   *
   * @return {object}
   */
  this.getPlaylistItem = function (index) {
    const playlistItem = (typeof index === 'number')
      ? SM.context.playlist[index]
      : SM.context.playlistItem;

    if (playlistItem) {
      return playlistItem;
    }

    return false;
  };

  /**
   * Retrieve the index of the current playlist item
   *
   * @return {number}
   */
  this.getPlaylistIndex = function () {
    return SM.context.playlistIndex;
  };

  /**
   * Retrieve the metadata for the currently loaded playlist
   *
   * @returns {Object}
   */
  this.getPlaylistMetadata = function () {
    return content.playlist.getPlaylistMetadata();
  };

  /**
   * Select the next item in the playlist
   */
  this.nextPlaylistItem = function () {
    if (SM.context.inAdBreak) return;

    logger.log('Action: nextPlaylistItem()', 'debug2');

    const resume = !this.isPaused();
    const retVal = content.playlist.nextPlaylistItem();

    if (retVal !== false && resume) {
      this.play();
    }

    return retVal;
  };

  /**
   * Select the previous item in the playlist
   */
  this.previousPlaylistItem = function () {
    if (SM.context.inAdBreak) return;

    logger.log('Action: previousPlaylistItem()', 'debug2');

    const resume = !this.isPaused();
    const retVal = content.playlist.previousPlaylistItem();

    if (retVal !== false && resume) {
      this.play();
    }

    return retVal;
  };

  /**
   * Select a playlist item by it's index
   *
   * @param {number} index
   */
  this.selectPlaylistItem = function (index) {
    if (SM.context.inAdBreak) return;

    logger.log(`Action: selectPlaylistItem(${JSON.stringify(index)})`, 'debug2');

    if (!content.playlist.hasPlaylistItem(index)) {
      this.log(`Failed to change playlist item: could not find a playlist item matching the identifier "${JSON.stringify(index)}"`, Logger.WARN);
      return -1;
    }

    // reset the timeline on playlist item change
    _timeline.clear();

    content.playlist.selectPlaylistItem(index);

    return index;
  };

  //
  // TEXT TRACKS
  //

  /**
   * Retrieve the available captions, chapters and subtitles text tracks on the player
   *
   * @param {string} kind Filter the text track by kind
   *
   * @return {object[]}
   */
  this.getTextTracks = function (kind) {
    if (typeof kind !== 'undefined') {
      return SM.context.textTracks.filter(function (track) {
        return track.kind === kind;
      });
    }

    return SM.context.textTracks;
  };

  /**
   * Retrieves a list of chapters from a loaded chapters text track
   *
   * @return {object[]}
   */
  this.getChapters = function () {
    return SM.context.chapters;
  };

  /**
   * Retrieve the current active chapter from the active chapters text track
   *
   * @return {object[]}
   */
  this.getCurrentChapter = function () {
    return SM.context.activeChapter;
  };

  /**
   * Retrieves a list of captions on the current active caption text tracks
   *
   * @return {object[]}
   */
  this.getCaptions = function () {
    return SM.context.captions;
  };

  /**
   * Retrieves a list of active captions on current active caption text track
   *
   * @return {object[]}
   */
  this.getCurrentCaptions = function () {
    return SM.context.activeCaptions;
  };

  /**
   * Retrieves a list of subtitles from the current active subtitle text track
   *
   * @return {object[]}
   */
  this.getSubtitles = function () {
    return SM.context.subtitles;
  };

  /**
   * Retrieves a list of active subtitles on the current active subtitle text track
   *
   * @return {Object[]}
   */
  this.getCurrentSubtitles = function () {
    return SM.context.activeSubtitles;
  };

  //
  // Advertising
  //

  /**
   * Retrieve the current playing advertisement
   *
   * @return {Object|null}
   */
  this.getCurrentAd = function () {
    return (advertising && advertising.getCurrentAd()) || null;
  };

  /**
   * Retrieve the position of the current playing advertisement
   *
   * @return {Number}
   */
  this.getAdPosition = function () {
    const position = (advertising && advertising.getPosition());
    return position === false ? 0 : position;
  };

  /**
   * Retrieve the duration of the current playing advertisement
   *
   * @return {Number}
   */
  this.getAdDuration = function () {
    const duration = (advertising && advertising.getDuration());
    return duration === false ? 0 : duration;
  };

  /**
   * Return the playback source of the current playing advertisement
   *
   * @return {Object|null}
   */
  this.getAdSource = function () {
    return (advertising && advertising.getSource()) || null;
  };

  /**
   * Skip the current playing advertisement
   */
  this.skipad = function () {
    logger.log('Action: skip()', 'debug2');
    SM.send(ApiActions.Skip);
  };

  /**
   * Retrieve the skip offset
   *
   * @return {Number}
   */
  this.getAdSkipOffset = function () {
    const offset = (advertising && advertising.getSkipOffset());
    return offset !== false ? offset : defaultOptions.ads.skipOffset;
  };

  /**
   * Check if an ad break has started
   *
   * @returns {Boolean}
   */
  this.inAdBreak = function () {
    return advertising && advertising.inAdBreak();
  };

  /**
   * Check if the ad break is skippable
   *
   * @return {Boolean}
   */
  this.isAdSkippable = function () {
    return advertising && advertising.isSkippable();
  };

  /**
   * Retrieve the remaining skip offset
   *
   * @return {Number}
   */
  this.getAdSkipOffsetRemaining = function () {
    const offsetRemaining = advertising && advertising.getRemainingSkipOffset();
    return offsetRemaining !== false ? offsetRemaining : defaultOptions.ads.skipOffset;
  };

  //
  // Helpers
  //

  this.log = logger.log;

  /**
   * Checks if the current playback source is a live source
   *
   * @return {boolean}
   */
  const liveContentTypes = [METADATA_TYPE_ADSWIZZ_STREAM, METADATA_TYPE_STREAM];
  this.isLive = function () {
    return api.getState() !== STATE_EMPTY && (content.isLive() || (SM.context.playlistItem && liveContentTypes.includes(SM.context.playlistItem.metadata.contentType)));
  };

  /**
   * Retrieve the bitrate of the currently selected quality level
   *
   * @return {number}
   */
  this.getBitrate = function () {
    return (SM.context.source && SM.context.source.bitrate) || 0;
  };

  /**
   * Retrieve the metadata for the current playlist item
   *
   * @return {object|false}
   */
  this.getMetadata = function () {
    return (SM.context.playlistItem && SM.context.playlistItem.metadata) || null;
  };

  /**
   * Retrieve the metadata of the now playing item for the current live stream
   *
   * @return {Object|false}
   */
  this.getNowPlayingItem = function () {
    return _nowPlaying.getCurrentEntry();
  };

  /**
   * Retrieve historic now playing items for the current live stream
   *
   * @return {Array}
   */
  this.getNowPlayingHistory = function () {
    return _nowPlaying.getHistory();
  };

  /**
   * Retrieve the current playback source of the player
   *
   * @return {object}
   */
  this.getSource = function () {
    return content.getSource();
  };

  /**
   * Retrieve the current state of the player
   *
   * @return {string}
   */
  this.getState = function () {
    return SM.state;
  };

  /**
   * Retrieve the current player error
   *
   * @return {Error|false}
   */
  this.getError = function () {
    return SM.context.error;
  };

  /**
   * Retrieve the SID of the player
   *
   * @return {String}
   */
  this.getSID = function Player_getSID () {
    return playerSid;
  };

  /**
   * Wait for the player to become ready before calling the callback
   *
   * @param {function} The callback to execute
   */
  this.ready = function (fn) {
    if (SM.context.ready) {
      return fn.call(this);
    }
    readyQueue.push(fn);
  };

  /**
   * Destroy the player and clean up resources
   */
  this.destroy = function () {
    logger.log('Action: destroy()', 'debug2');
    advertising && advertising.destroy();
    content.destroy();
  };

  /**
   * Trigger a download of the current playing source
   */
  this.download = function () {
    let downloadUrl = '';

    const source = this.getSource();
    if (source) {
      downloadUrl = (source.download || source.url);
    }

    if (!downloadUrl) return;

    const link = document.createElement('a');

    // iOS devices don't allow downloads
    if (environment.platform.isIOS === false && link.download !== undefined) {
      link.setAttribute('href', downloadUrl);
      link.setAttribute('target', '_blank');
      link.setAttribute('download', filename(downloadUrl));
      link.click();
    } else {
      // HTML5 download attribute not supported, open in new window
      window.open(downloadUrl, '_blank');
    }

    // trigger download event
    content.emit({ type: EVENT_DOWNLOAD });
  };

  //
  // Events
  //

  /**
   * Bind a new event handler
   *
   * @param {string|string[]} type The event type or an array of event types to bind to
   * @param {function}        fn   The event handler
   */
  this.on = function (type, callback) {
    if (Array.isArray(type)) {
      type.forEach(type => events.on(type, callback));
      return;
    }

    return events.on(type, callback);
  };

  /**
   * Unbind from an existing event handler
   *
   * @param {string|string[]} type The event type or an array of event types to unbind from
   * @param {function}        fn   The event handler
   */
  this.off = function (type, callback) {
    if (Array.isArray(type)) {
      type.forEach(type => events.off(type, callback));
      return;
    }

    return events.off(type, callback);
  };

  /**
   * Bind an event handler that triggers only once
   *
   * @param {string|string[]} type The event type or an array of event types to bind to
   * @param {function}        fn   The event handler
   */
  this.one = function (type, callback) {
    if (Array.isArray(type)) {
      type.forEach(type => events.one(type, callback));
      return;
    }

    return events.one(type, callback);
  };

  // add the debug method when building in debug mode
  if (__DEBUG__) { /* eslint-disable-line */
    this.debug = function () {
      return {
        state: SM,
        settings: settings,
        content: content,
        advertising: advertising,
        analytics: analytics,
        timeline: _timeline,
        computed: computed,
        logger: logger
      };
    };
  }

  // analytics added here because it needs the "on" method on the player
  const analytics = Analytics(content, logger, computed, options);

  // add advertising support to player
  if (options.ads.enabled) {
    content.tracking.settings.trackStaticAds = true;

    advertising = new Advertising(api, content, {
      logger,
      enabled: options.ads.enabled,
      serverURL: options.ads.requestURL,
      timeout: options.ads.requestTimeout,
      skipOffset: options.ads.skipOffset,
      rerollTimeout: Math.max(120, options.ads.rerollTimeout),
      computed: computed,
      timeline: _timeline,
      lastAdRollTime: settings.get('lastAdRollTime'),

      adContainer: options.ads.adContainer,
      pofToken: options.ads.pofToken || null
    });
    const advertisingAnalytics = Analytics(advertising, logger, computed, options);

    // forward advertising events to the state machine
    advertising.observe(function _advertsEventObserver (event) {
      logger.log(`AdvertsEvent: ${event.type} (${SM.state})`, 'debug2');
      SM.send(prefixAdvertisingEvent(event));
    });
  }

  // forward transition events from the state machine to the api as a state event
  SM.on(StateEvents.Transition, function _stateTransition (event) {
    events.emitAsync({
      type: EVENT_STATE,
      previousState: event.previousState,
      currentState: event.currentState
    });
  });

  // forward content player events to the state machine
  content.observe(function _contentEventObserver (event) {
    logger.log(`ContentEvent: ${event.type} (${SM.state})`, 'debug2');
    SM.send(prefixPlayerEvent(event));
  });

  SM.transition(STATE_EMPTY);

  if (options.source) {
    api.load(options.source, options.sourceOptions);
  }
}

/**
 * Determine whether or not playback is supported in the current environment
 *
 * @return {boolean}
 */
Player.supported = function Player_supported () {
  const audio = document.createElement('audio');
  return audio && !!audio.canPlayType;
};

Player.isVolumeSupported = (function () {
  const checkVolumeSupport = _ => {
    const ua = navigator.userAgent.toLowerCase();
    const noVolume = /ipad|iphone|ipod|android|blackberry|windows ce|windows phone|webos|playbook/.exec(ua);

    if (noVolume) {
      if (noVolume[0] === 'android' && /firefox/.test(ua)) {
        return true;
      } else {
        return false;
      }
    }

    if (!window.Audio) {
      return false;
    }

    const audio = new window.Audio();
    audio.volume = 0.5;

    return audio.volume === 0.5;
  };

  let hasVolumeSupport = null;

  return _ => {
    if (hasVolumeSupport === null) {
      hasVolumeSupport = checkVolumeSupport();
    }
    return hasVolumeSupport;
  };
})();

/**
 * Get the current version of the player
 *
 * @return {string}
 */
Player.version = function Player_version () {
  return VERSION;
};

/**
 * Retrieve information about the current environment
 *
 * @return {Object}
 */
Player.getEnvironment = function Player_getEnvironment () {
  return environment;
};

/**
 * Retrieve the UUID of the current page
 *
 * @return {String}
 */
Player.getUUID = function Player_getUUID () {
  return uuid();
};

/**
 * Retrieve the referer url
 *
 * @return {String}
 */
Player.getReferer = function Player_getReferer () {
  return referrer();
};

/**
 * Clear settings stored in local storage
 */
Player.clearSettings = function Player_clearSettings () {
  settings.clear();
};

Player.formatSeconds = formatSeconds;

Player.Error = PlayerError;

Player.Utils = Object.create(null);
Player.Utils.filesize = filesize;
Player.Utils.fetchVttData = loadRemoteTextTrack.bind({ logger: Logger });
Player.Utils.parseVttData = parseVttData.bind({ logger: Logger });
Player.Logger = Logger;

Logger.log('[iono.Player] Player version ' + Player.version());

export default Player;
